import { IRemoteEvent, TranscriptionStatus } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { createContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { MrryDirection, MrryMessageType, MrryModal } from '../../components/MrryModal';
import * as SharedAppState from '../../models/AppState';
import { IWebsocketSubscription } from '../../viewModels/websockets';
import { styleSheet } from './styles';

export const TranscriptionEventsContext = createContext<{
	eventPayload: IRemoteEvent<ITranscriptionWsEvent>[];
}>(null);

export interface ITranscriptionWsEvent {
	status: TranscriptionStatus;
	creationContext?: {
		resources?: {
			phoneCall?: string;
		};
	};
}

interface IMrryState {
	content: {
		phoneCallId: string;
		status: string;
	}[];
	open: boolean;
	payload: IRemoteEvent<ITranscriptionWsEvent>[];
}

const mrryInitialState: IMrryState = {
	content: null,
	open: false,
	payload: null,
};

export const useTranscriptionWsEvent = (callback: (payload: IRemoteEvent<ITranscriptionWsEvent>[]) => void) => {
	const websockets = SharedAppState.AppState[SharedAppState.WebsocketsViewModelKey];

	useEffect(() => {
		const subscription: IWebsocketSubscription<ITranscriptionWsEvent> = {
			callback,
			events: ['TranscriptionEvent'],
			name: 'TranscriptionEventsProvider',
		};
		websockets.subscribe(subscription);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return () => {
		websockets.unsubscribe('TranscriptionEventsProvider');
	};
};

export const TranscriptionEventsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const [showMrry, setMrryState] = useState<IMrryState>(mrryInitialState);

	const history = useHistory();

	const unsubscribe = useTranscriptionWsEvent(payload => {
		setMrryState({
			content: [...payload].map(item => ({
				phoneCallId: item.value.creationContext.resources.phoneCall,
				status: item.value.status,
			})),
			open: true,
			payload,
		});
	});
	useEffect(() => {
		return () => {
			unsubscribe();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleCloseMrry = () => setMrryState(mrryInitialState);

	const goto = (phoneCallId: string) => () => {
		history.push(`/call-log/${phoneCallId || ''}`);
	};

	return (
		<TranscriptionEventsContext.Provider
			value={{
				eventPayload: showMrry.payload,
			}}
		>
			{showMrry.open && (
				<MrryModal
					fromDirection={MrryDirection.FromRightTop}
					internalCloseButton={true}
					message={{
						content: (
							<small>
								<p className={css(styleSheet.title)}>
									You have {showMrry?.content?.length || 0} transcription(s) finished!
								</p>
								{showMrry?.content?.map((item, index) => {
									const className =
										item.status === TranscriptionStatus.Completed
											? styleSheet.statusCompleted
											: styleSheet.statusFailed;

									return (
										<p
											key={item.phoneCallId}
											className={css(styleSheet.item)}
											onClick={goto(item.phoneCallId)}
											role='button'
										>
											<strong>{index + 1} - </strong>
											<i>
												Status: <span className={css(className)}>{item.status}</span> - Click for more.
											</i>
										</p>
									);
								})}
							</small>
						),
						type: MrryMessageType.Success,
					}}
					onRequestClose={handleCloseMrry}
				/>
			)}

			{children}
		</TranscriptionEventsContext.Provider>
	);
};
